import { Document, Image, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Dayjs } from 'dayjs';
import { t } from 'i18next';

import { buildQuoteLineItems } from '~components/Project/QuotePdf/buildQuoteLineItems';
import {
  COMBINED_RATE_MARKER,
  formatDate,
  formatRateWithMarker,
} from '~components/Project/QuotePdf/utils';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import { Company } from '~hooks/useCompany/models';
import { MaterialRate } from '~hooks/useMaterialRates/models';
import { Project } from '~hooks/useProjects/models';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import { getProjectStateLabel } from '~pages/Sales/Projects/v2/projectTabs';
import { getRateTypeLabel } from '~pages/Settings/RatesManagement/rateUtils';
import { Nullable } from '~types/Nullable';
import { formatPercentage } from '~utils/utilFunctions';

interface QuotePdfProps {
  project: Project;
  company: Nullable<Company>;
  companyLogoBase64?: string;
  currencyFormatter: (value: number | string) => string;
  materialRates?: MaterialRate[];
  addOnAssignments?: AddOnAssignment[];
  treadLogoBase64?: string;
  // Rate cards for the project. Rendered as the Freight section, extended to a
  // line total wherever the card carries an estimated quantity.
  siteRateSchedules?: SiteRateSchedule[];
  dispatchNumberLabel?: string;
}

// Colors matching Job Card PDF and Settlement PDF
const DARK_GRAY = '#6b7280';
const MEDIUM_GRAY = '#374151';
const LIGHT_GRAY = '#f9fafb';
const BORDER_GRAY = '#d1d5db';
const WHITE = '#ffffff';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 9,
    position: 'relative',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 0,
  },
  headerLeft: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  companyLogo: {
    width: 72,
    height: 72,
    marginBottom: 6,
    objectFit: 'contain',
    objectPosition: 'left top',
  },
  companyTextBlock: {
    flexDirection: 'column',
  },
  companyName: {
    fontSize: 11,
    fontWeight: 'normal',
    color: MEDIUM_GRAY,
  },
  companyAddress: {
    fontSize: 8,
    color: DARK_GRAY,
    marginTop: 1,
  },
  headerRight: {
    alignItems: 'flex-end',
  },
  quoteTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: MEDIUM_GRAY,
  },
  quoteId: {
    fontSize: 9,
    color: DARK_GRAY,
  },
  headerRule: {
    borderBottomWidth: 1,
    borderBottomColor: BORDER_GRAY,
    marginTop: 8,
    marginBottom: 8,
  },
  subHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  projectName: {
    fontSize: 12,
    fontWeight: 'bold',
    color: MEDIUM_GRAY,
  },
  subHeaderMeta: {
    alignItems: 'flex-end',
  },
  subHeaderMetaText: {
    fontSize: 9,
    color: DARK_GRAY,
  },
  sectionHeader: {
    backgroundColor: DARK_GRAY,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginBottom: 0,
  },
  sectionHeaderText: {
    fontSize: 8,
    fontWeight: 'bold',
    color: WHITE,
  },
  // Grid rows (used by Customer, Details, Contacts, Order Config)
  gridRow: {
    flexDirection: 'row',
  },
  gridCell: {
    flex: 1,
    backgroundColor: LIGHT_GRAY,
    borderWidth: 1,
    borderColor: BORDER_GRAY,
    borderTopWidth: 0,
    paddingVertical: 4,
    paddingHorizontal: 8,
    minHeight: 28,
  },
  gridCellLabel: {
    fontSize: 7,
    color: DARK_GRAY,
  },
  gridCellValue: {
    fontSize: 9,
    color: MEDIUM_GRAY,
    marginTop: 1,
  },
  sectionSpacing: {
    marginBottom: 10,
  },
  sitesContent: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: BORDER_GRAY,
    borderTopWidth: 0,
    padding: 8,
  },
  sitesColumn: {
    width: '50%',
    paddingRight: 8,
  },
  siteBlock: {
    marginBottom: 4,
  },
  siteBlockLast: {
    marginBottom: 0,
  },
  siteLabel: {
    fontSize: 7,
    color: DARK_GRAY,
    marginBottom: 1,
  },
  siteValue: {
    fontSize: 9,
    color: MEDIUM_GRAY,
  },
  materialsTableHeader: {
    flexDirection: 'row',
    backgroundColor: DARK_GRAY,
    paddingVertical: 5,
    paddingHorizontal: 8,
  },
  materialsTableHeaderCell: {
    fontSize: 8,
    fontWeight: 'bold',
    color: WHITE,
  },
  materialsRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: BORDER_GRAY,
    borderTopWidth: 0,
  },
  materialsRowAlt: {
    flexDirection: 'row',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: BORDER_GRAY,
    borderTopWidth: 0,
    backgroundColor: LIGHT_GRAY,
  },
  materialsCell: {
    fontSize: 9,
    color: MEDIUM_GRAY,
  },
  colMaterial: { width: '24%' },
  colSite: { width: '16%' },
  colQty: { width: '10%', textAlign: 'right', paddingRight: 4 },
  colUnit: { width: '10%' },
  colRate: { width: '22%', textAlign: 'right' },
  colLineTotal: { width: '18%', textAlign: 'right' },
  materialsSubtotalRow: {
    flexDirection: 'row',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: BORDER_GRAY,
    borderTopWidth: 0,
  },
  // Label spans every column except the line-total column (18%) so the
  // subtotal amount aligns under the Line Total column.
  colSubtotalLabel: { width: '82%', textAlign: 'right', paddingRight: 4 },
  materialsSubtotalCell: {
    fontSize: 9,
    fontWeight: 'bold',
    color: MEDIUM_GRAY,
  },
  colDeliveredEquip: { width: '15%' },
  colDeliveredMaterial: { width: '21%' },
  colDeliveredSource: { width: '22%' },
  colDeliveredQty: { width: '13%', textAlign: 'right', paddingRight: 4 },
  colDeliveredRate: { width: '14%', textAlign: 'right' },
  colDeliveredSalesPrice: { width: '15%', textAlign: 'right' },
  // Label spans every column except the sales-price column (15%) so the
  // subtotal amount aligns under Sales Price.
  colDeliveredSubtotalLabel: { width: '85%', textAlign: 'right', paddingRight: 4 },
  colFreightEquip: { width: '17%' },
  colFreightDesc: { width: '21%' },
  colFreightRoute: { width: '22%' },
  colFreightQty: { width: '14%', textAlign: 'right', paddingRight: 4 },
  colFreightRate: { width: '14%', textAlign: 'right' },
  colFreightLineTotal: { width: '12%', textAlign: 'right' },
  // Label spans every column except the line-total column (12%) so the
  // subtotal amount aligns under Line Total.
  colFreightSubtotalLabel: { width: '88%', textAlign: 'right', paddingRight: 4 },
  colAddOn: { width: '34%' },
  colAddOnQty: { width: '18%', textAlign: 'right', paddingRight: 4 },
  colAddOnBasis: { width: '28%' },
  colAddOnAmount: { width: '20%', textAlign: 'right' },
  // Label spans every column except the amount column (20%) so the subtotal
  // amount aligns under the Amount column.
  colAddOnSubtotalLabel: { width: '80%', textAlign: 'right', paddingRight: 4 },
  freightNote: {
    fontSize: 8,
    color: DARK_GRAY,
    fontStyle: 'italic',
    marginTop: 4,
  },
  pricingContent: {
    backgroundColor: '#f6f6f6',
    borderWidth: 1,
    borderColor: BORDER_GRAY,
    borderTopWidth: 0,
    padding: 8,
  },
  pricingRow: {
    flexDirection: 'row',
    marginBottom: 2,
  },
  pricingLabel: {
    fontSize: 9,
    color: MEDIUM_GRAY,
    width: 120,
  },
  pricingValue: {
    fontSize: 9,
    color: MEDIUM_GRAY,
  },
  notesContent: {
    borderWidth: 1,
    borderColor: BORDER_GRAY,
    borderTopWidth: 0,
    padding: 8,
  },
  notesSubHeader: {
    fontSize: 8,
    fontWeight: 'bold',
    color: DARK_GRAY,
    marginBottom: 2,
  },
  notesText: {
    fontSize: 9,
    color: MEDIUM_GRAY,
    marginBottom: 8,
  },
  footerRule: {
    borderBottomWidth: 1,
    borderBottomColor: BORDER_GRAY,
    marginTop: 20,
    marginBottom: 12,
  },
  termsText: {
    fontSize: 9,
    color: MEDIUM_GRAY,
    padding: 8,
    whiteSpace: 'pre-wrap',
  },
  signatureHeading: {
    fontSize: 8,
    fontWeight: 'bold',
    color: MEDIUM_GRAY,
    marginTop: 12,
  },
  signatureContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 12,
    marginBottom: 12,
  },
  signatureBlock: {
    flexDirection: 'column',
    width: '48%',
  },
  signatureLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    color: DARK_GRAY,
    marginBottom: 14,
  },
  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: MEDIUM_GRAY,
    height: 1,
  },
  footerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  footerLogo: {
    width: 100,
    height: 20,
    objectFit: 'contain',
    marginRight: 10,
  },
  footerLeftText: {
    flexDirection: 'column',
  },
  footerTagline: {
    fontSize: 7,
    color: DARK_GRAY,
    marginBottom: 1,
  },
  footerUrl: {
    fontSize: 7,
    fontWeight: 'bold',
    color: '#1e40af',
  },
  footerRight: {
    alignItems: 'flex-end',
  },
  footerCta: {
    fontSize: 8,
    fontWeight: 'bold',
    color: MEDIUM_GRAY,
    marginBottom: 2,
  },
  footerCtaSub: {
    fontSize: 7,
    color: DARK_GRAY,
  },
});

function GridRow({
  cells,
  columns = 2,
}: {
  cells: { label: string; value: string }[];
  columns?: number;
}) {
  const filledCells = [...cells];
  while (filledCells.length < columns) {
    filledCells.push({ label: '', value: '' });
  }
  return (
    <View style={styles.gridRow}>
      {filledCells.map((cell, i) => (
        <View key={i} style={styles.gridCell}>
          <Text style={styles.gridCellLabel}>{cell.label}</Text>
          <Text style={styles.gridCellValue}>{cell.value}</Text>
        </View>
      ))}
    </View>
  );
}

type GridCell = { label: string; value: string };

// formatDate renders an em-dash for a missing date, which a grid cell must
// treat as absent rather than as a value worth its own box.
const optionalDate = (date: Nullable<Dayjs | string>) => (date ? formatDate(date) : '');

const hasAnyValue = (cells: GridCell[]) => cells.some((cell) => cell.value.length > 0);

// Fields the project never filled in are dropped rather than printed as an
// empty box, so the remaining cells reflow to fill each row.
function GridSection({ cells, columns }: { cells: GridCell[]; columns: number }) {
  const presentCells = cells.filter((cell) => cell.value.length > 0);
  return (
    <>
      {Array.from({ length: Math.ceil(presentCells.length / columns) }, (_, rowIdx) => (
        <GridRow
          key={rowIdx}
          columns={columns}
          cells={presentCells.slice(rowIdx * columns, rowIdx * columns + columns)}
        />
      ))}
    </>
  );
}

export default function QuotePdf({
  project,
  company,
  companyLogoBase64,
  currencyFormatter,
  materialRates = [],
  addOnAssignments = [],
  treadLogoBase64,
  siteRateSchedules = [],
  dispatchNumberLabel = t('project.quote_pdf.dispatch_number'),
}: QuotePdfProps) {
  const salesperson = project.salespeople?.[0];

  // The account's primary contact is the durable customer contact. Fall back to
  // the project's sales contact (the one contact the order form populates),
  // then the legacy project contact field.
  const customerContact =
    project.account?.primaryContact ?? project.salesContact ?? project.contact;

  const contacts: {
    label: string;
    contact: Nullable<{ name: string; phone: string }>;
  }[] = [
    {
      label: t('project.quote_pdf.sales_contact'),
      contact: project.salesContact,
    },
    {
      label: t('project.quote_pdf.foreman_contact'),
      contact: project.foremanContact,
    },
    {
      label: t('project.quote_pdf.supervisor_contact'),
      contact: project.supervisorContact,
    },
    {
      label: t('project.quote_pdf.collaborator_contact'),
      contact: project.collaboratorContact,
    },
  ].filter((c) => c.contact);

  const customerCells: GridCell[] = [
    { label: t('project.quote_pdf.account'), value: project.account?.name ?? '' },
    { label: t('project.quote_pdf.contact'), value: customerContact?.name ?? '' },
    {
      label: t('project.quote_pdf.contact_email'),
      value: customerContact?.email ?? '',
    },
    {
      label: t('project.quote_pdf.contact_phone'),
      value: customerContact?.phone ?? '',
    },
  ];

  const projectDetailCells: GridCell[] = [
    {
      label: t('project.quote_pdf.status'),
      value: project.state ? getProjectStateLabel(project.state) : '',
    },
    {
      label: t('project.quote_pdf.effective_date'),
      value: optionalDate(project.effectiveAt),
    },
    {
      label: t('project.quote_pdf.expires_date'),
      value: optionalDate(project.expiresAt),
    },
    {
      label: t('project.quote_pdf.created_date'),
      value: optionalDate(project.createdAt),
    },
    {
      label: t('project.quote_pdf.service_class'),
      value: project.serviceClass?.name ?? '',
    },
    {
      label: t('project.quote_pdf.department'),
      value: project.department?.name ?? '',
    },
    {
      label: dispatchNumberLabel,
      value: project.dispatchNumber?.name ?? '',
    },
    {
      label: t('project.quote_pdf.po_job_number'),
      value: project.poJobNumber || '',
    },
    {
      label: t('project.quote_pdf.external_id'),
      value: project.externalId || '',
    },
    {
      label: t('project.quote_pdf.requester'),
      value: project.requester?.fullName ?? '',
    },
  ];

  const pickupWaypoints = project.pickUpWayPoints ?? [];
  const dropoffWaypoints = project.dropOffWayPoints ?? [];
  const siteColumns = [
    {
      type: 'pickup',
      label: t('project.quote_pdf.pickup_site'),
      waypoints: pickupWaypoints,
    },
    {
      type: 'dropoff',
      label: t('project.quote_pdf.dropoff_site'),
      waypoints: dropoffWaypoints,
    },
  ];
  const hasSites = pickupWaypoints.length > 0 || dropoffWaypoints.length > 0;

  const {
    addOnRows,
    addOnSubtotal,
    canSubtotalDelivered,
    combinedAnyFreightRate,
    deliveredPriceRows,
    deliveredPricingIncludesFreight,
    deliveredSubtotal,
    freightRows,
    freightSubtotal,
    hasAnyAddOnAmount,
    hasAnyFreightLineTotal,
    hasAnyMaterialLineTotal,
    hasComputableTax,
    hasDeliveredPricing,
    hasFreight,
    hasMaterials,
    materialRows,
    materialSubtotal,
    shouldCombineMaterialAndHaul,
    taxAmount,
    taxBasisSuffix,
  } = buildQuoteLineItems({
    project,
    currencyFormatter,
    materialRates,
    siteRateSchedules,
    addOnAssignments,
  });

  const orderConfigItems: { label: string; value: string }[] = [
    {
      label: t('project.quote_pdf.order_qty'),
      value: project.orderQuantity
        ? `${project.orderQuantity}${project.orderUnitOfMeasure ? ` ${project.orderUnitOfMeasure.name}` : ''}`
        : '',
    },
    {
      label: t('project.quote_pdf.job_qty'),
      value: project.jobQuantity
        ? `${project.jobQuantity}${project.jobUnitOfMeasure ? ` ${project.jobUnitOfMeasure.name}` : ''}`
        : '',
    },
    {
      label: t('project.quote_pdf.truck_count'),
      value: project.truckCount?.toString() ?? '',
    },
    {
      label: t('project.quote_pdf.loads_per_truck'),
      value: project.loadsPerTruck?.toString() ?? '',
    },
    {
      label: t('project.quote_pdf.units_per_hour'),
      value: project.unitsPerHour?.toString() ?? '',
    },
  ].filter((item) => item.value.length > 0);
  const hasOrderConfig = orderConfigItems.length > 0;

  // Pricing — use customerRate object if available, otherwise fall back to scalar fields
  const hasCustomerRate =
    Boolean(project.customerRate) ||
    (project.customerRateValue !== null && project.customerRateValue !== undefined);
  let customerRateDisplay = '';
  if (project.customerRate) {
    const val =
      project.customerRateValue !== null && project.customerRateValue !== undefined
        ? currencyFormatter(project.customerRateValue)
        : '';
    const typeLabel = project.customerRate.readableTypeValue ?? '';
    customerRateDisplay = `${val}${typeLabel ? ` ${typeLabel}` : ''}`;
  } else if (
    project.customerRateValue !== null &&
    project.customerRateValue !== undefined
  ) {
    const val = currencyFormatter(project.customerRateValue);
    const typeLabel = project.customerRateType
      ? getRateTypeLabel(project.customerRateType)
      : '';
    customerRateDisplay = `${val}${typeLabel ? ` ${typeLabel}` : ''}`;
  }
  const hasAddOns = addOnAssignments.length > 0;
  // Add-ons render in their own detailed section (below), so pricing is just
  // the customer rate and tax.
  const hasPricing = hasCustomerRate || hasComputableTax;

  const projectNotes = project.projectNotes;
  const orderNotes = project.orderNotes;
  const hasNotes = projectNotes || orderNotes;

  // BE owns the precedence: project_terms ?? company.default_project_terms ?? null.
  // `company` prop kept on the interface only for header rendering; terms come from the project.
  const effectiveTerms = project.effectiveProjectTerms;

  const hasProjectName = Boolean(project.name);
  const hasSalesperson = Boolean(salesperson?.fullName);
  const hasSubHeader = hasProjectName || hasSalesperson;

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {companyLogoBase64 ? (
              <Image src={companyLogoBase64} style={styles.companyLogo} />
            ) : null}
            <View style={styles.companyTextBlock}>
              <Text style={styles.companyName}>{company?.legalName ?? '\u2014'}</Text>
              {company?.address?.thoroughfare ? (
                <Text style={styles.companyAddress}>{company.address.thoroughfare}</Text>
              ) : null}
              {company?.address?.locality || company?.address?.administrativeArea ? (
                <Text style={styles.companyAddress}>
                  {[
                    company.address.locality,
                    company.address.administrativeArea,
                    company.address.postalCode,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Text>
              ) : null}
            </View>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.quoteTitle}>{t('project.quote_pdf.title')}</Text>
            <Text style={styles.quoteId}>{project.projectId}</Text>
          </View>
        </View>

        <View style={styles.headerRule} />

        {hasSubHeader ? (
          <View style={styles.subHeader}>
            <Text style={styles.projectName}>{project.name}</Text>
            <View style={styles.subHeaderMeta}>
              {hasSalesperson ? (
                <Text style={styles.subHeaderMetaText}>
                  {salesperson?.phone
                    ? t('project.quote_pdf.prepared_by_with_phone', {
                        name: salesperson.fullName,
                        phone: salesperson.phone,
                      })
                    : t('project.quote_pdf.prepared_by', {
                        name: salesperson?.fullName,
                      })}
                </Text>
              ) : null}
              {project.effectiveAt ? (
                <Text style={styles.subHeaderMetaText}>
                  {t('project.quote_pdf.quoted_date', {
                    date: formatDate(project.effectiveAt),
                  })}
                </Text>
              ) : null}
            </View>
          </View>
        ) : null}

        {hasAnyValue(customerCells) || project.account?.billingAddress ? (
          <View style={styles.sectionSpacing}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>
                {t('project.quote_pdf.customer')}
              </Text>
            </View>
            <GridSection columns={2} cells={customerCells} />
            {project.account?.billingAddress ? (
              <GridRow
                columns={1}
                cells={[
                  {
                    label: t('project.quote_pdf.address'),
                    value:
                      project.account.billingAddress.fullAddress ||
                      project.account.billingAddress.streetAddress ||
                      '',
                  },
                ]}
              />
            ) : null}
          </View>
        ) : null}

        {contacts.length > 0 ? (
          <View style={styles.sectionSpacing}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>
                {t('project.quote_pdf.contacts')}
              </Text>
            </View>
            {contacts.map((c) => (
              <GridSection
                key={c.label}
                columns={2}
                cells={[
                  { label: c.label, value: c.contact?.name ?? '' },
                  {
                    label: t('project.quote_pdf.contact_phone'),
                    value: c.contact?.phone ?? '',
                  },
                ]}
              />
            ))}
          </View>
        ) : null}

        {hasAnyValue(projectDetailCells) ? (
          <View style={styles.sectionSpacing}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>
                {t('project.quote_pdf.project_details')}
              </Text>
            </View>
            <GridSection columns={4} cells={projectDetailCells} />
          </View>
        ) : null}

        {hasSites ? (
          <View style={styles.sectionSpacing}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>{t('project.quote_pdf.sites')}</Text>
            </View>
            <View style={styles.sitesContent}>
              {siteColumns.map((column) => (
                <View key={column.type} style={styles.sitesColumn}>
                  {column.waypoints.map((waypoint, idx) => {
                    const isLast = idx === column.waypoints.length - 1;
                    const site = waypoint.site;
                    const value = site?.fullAddress
                      ? `${site.name}\n${site.fullAddress}`
                      : (site?.name ?? '\u2014');
                    return (
                      <View
                        key={`${column.type}-${idx}`}
                        style={isLast ? styles.siteBlockLast : styles.siteBlock}
                      >
                        <Text style={styles.siteLabel}>{column.label}</Text>
                        <Text style={styles.siteValue}>{value}</Text>
                      </View>
                    );
                  })}
                </View>
              ))}
            </View>
          </View>
        ) : null}

        {shouldCombineMaterialAndHaul && hasDeliveredPricing ? (
          <View style={styles.sectionSpacing}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>
                {t('project.quote_pdf.delivered_pricing')}
              </Text>
            </View>
            <View style={styles.materialsTableHeader}>
              <Text style={[styles.materialsTableHeaderCell, styles.colDeliveredEquip]}>
                {t('project.quote_pdf.equipment_type')}
              </Text>
              <Text
                style={[styles.materialsTableHeaderCell, styles.colDeliveredMaterial]}
              >
                {t('project.quote_pdf.material_name')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colDeliveredSource]}>
                {t('project.quote_pdf.source')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colDeliveredQty]}>
                {t('project.quote_pdf.quantity')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colDeliveredRate]}>
                {t('project.quote_pdf.rate')}
              </Text>
              <Text
                style={[styles.materialsTableHeaderCell, styles.colDeliveredSalesPrice]}
              >
                {t('project.quote_pdf.sales_price')}
              </Text>
            </View>
            {deliveredPriceRows.map((row, index) => (
              <View
                key={row.id}
                style={index % 2 === 0 ? styles.materialsRow : styles.materialsRowAlt}
              >
                <Text style={[styles.materialsCell, styles.colDeliveredEquip]}>
                  {row.equipmentType || '—'}
                </Text>
                <Text style={[styles.materialsCell, styles.colDeliveredMaterial]}>
                  {row.material || '—'}
                </Text>
                <Text style={[styles.materialsCell, styles.colDeliveredSource]}>
                  {row.source || '—'}
                </Text>
                <Text style={[styles.materialsCell, styles.colDeliveredQty]}>
                  {row.quantity || '—'}
                </Text>
                <Text style={[styles.materialsCell, styles.colDeliveredRate]}>
                  {formatRateWithMarker(row.rate, row.rateIsCombined)}
                </Text>
                <Text style={[styles.materialsCell, styles.colDeliveredSalesPrice]}>
                  {row.salesPrice || '—'}
                </Text>
              </View>
            ))}
            {canSubtotalDelivered ? (
              <View style={styles.materialsSubtotalRow}>
                <Text
                  style={[styles.materialsSubtotalCell, styles.colDeliveredSubtotalLabel]}
                >
                  {t('project.quote_pdf.delivered_subtotal')}
                </Text>
                <Text
                  style={[styles.materialsSubtotalCell, styles.colDeliveredSalesPrice]}
                >
                  {currencyFormatter(deliveredSubtotal.toNumber())}
                </Text>
              </View>
            ) : null}
            {combinedAnyFreightRate ? (
              <Text style={styles.freightNote}>
                {`${COMBINED_RATE_MARKER} ${t('project.quote_pdf.combined_rates_note')}`}
              </Text>
            ) : null}
            {deliveredPricingIncludesFreight ? (
              <Text style={styles.freightNote}>
                {t('project.quote_pdf.freight_rates_note')}
              </Text>
            ) : null}
          </View>
        ) : null}

        {!shouldCombineMaterialAndHaul && hasMaterials ? (
          <View style={styles.sectionSpacing}>
            <View style={styles.materialsTableHeader}>
              <Text style={[styles.materialsTableHeaderCell, styles.colMaterial]}>
                {t('project.quote_pdf.material_name')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colSite]}>
                {t('project.quote_pdf.site')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colQty]}>
                {t('project.quote_pdf.quantity')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colUnit]}>
                {t('project.quote_pdf.unit')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colRate]}>
                {t('project.quote_pdf.rate')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colLineTotal]}>
                {t('project.quote_pdf.line_total')}
              </Text>
            </View>
            {materialRows.map((row, index) => (
              <View
                key={row.id}
                style={index % 2 === 0 ? styles.materialsRow : styles.materialsRowAlt}
              >
                <Text style={[styles.materialsCell, styles.colMaterial]}>{row.name}</Text>
                <Text style={[styles.materialsCell, styles.colSite]}>
                  {row.site || '\u2014'}
                </Text>
                <Text style={[styles.materialsCell, styles.colQty]}>
                  {row.quantity || '\u2014'}
                </Text>
                <Text style={[styles.materialsCell, styles.colUnit]}>
                  {row.unit || '\u2014'}
                </Text>
                <Text style={[styles.materialsCell, styles.colRate]}>
                  {row.rate || '\u2014'}
                </Text>
                <Text style={[styles.materialsCell, styles.colLineTotal]}>
                  {row.lineTotal || '\u2014'}
                </Text>
              </View>
            ))}
            {hasAnyMaterialLineTotal ? (
              <View style={styles.materialsSubtotalRow}>
                <Text style={[styles.materialsSubtotalCell, styles.colSubtotalLabel]}>
                  {t('project.quote_pdf.material_subtotal')}
                </Text>
                <Text style={[styles.materialsSubtotalCell, styles.colLineTotal]}>
                  {currencyFormatter(materialSubtotal.toNumber())}
                </Text>
              </View>
            ) : null}
          </View>
        ) : null}

        {!shouldCombineMaterialAndHaul && hasFreight ? (
          <View style={styles.sectionSpacing}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>
                {t('project.quote_pdf.freight')}
              </Text>
            </View>
            <View style={styles.materialsTableHeader}>
              <Text style={[styles.materialsTableHeaderCell, styles.colFreightEquip]}>
                {t('project.quote_pdf.equipment_type')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colFreightDesc]}>
                {t('project.quote_pdf.description')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colFreightRoute]}>
                {t('project.quote_pdf.route')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colFreightQty]}>
                {t('project.quote_pdf.quantity')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colFreightRate]}>
                {t('project.quote_pdf.rate')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colFreightLineTotal]}>
                {t('project.quote_pdf.line_total')}
              </Text>
            </View>
            {freightRows.map((row, index) => (
              <View
                key={row.id}
                style={index % 2 === 0 ? styles.materialsRow : styles.materialsRowAlt}
              >
                <Text style={[styles.materialsCell, styles.colFreightEquip]}>
                  {row.equipType || '—'}
                </Text>
                <Text style={[styles.materialsCell, styles.colFreightDesc]}>
                  {row.description || '—'}
                </Text>
                <Text style={[styles.materialsCell, styles.colFreightRoute]}>
                  {row.route || '—'}
                </Text>
                <Text style={[styles.materialsCell, styles.colFreightQty]}>
                  {row.quantity || '—'}
                </Text>
                <Text style={[styles.materialsCell, styles.colFreightRate]}>
                  {formatRateWithMarker(row.rate, row.rateIsCombined)}
                </Text>
                <Text style={[styles.materialsCell, styles.colFreightLineTotal]}>
                  {row.lineTotal || '—'}
                </Text>
              </View>
            ))}
            {hasAnyFreightLineTotal ? (
              <View style={styles.materialsSubtotalRow}>
                <Text
                  style={[styles.materialsSubtotalCell, styles.colFreightSubtotalLabel]}
                >
                  {t('project.quote_pdf.freight_subtotal')}
                </Text>
                <Text style={[styles.materialsSubtotalCell, styles.colFreightLineTotal]}>
                  {currencyFormatter(freightSubtotal.toNumber())}
                </Text>
              </View>
            ) : null}
            {combinedAnyFreightRate ? (
              <Text style={styles.freightNote}>
                {`${COMBINED_RATE_MARKER} ${t('project.quote_pdf.combined_rates_note')}`}
              </Text>
            ) : null}
            <Text style={styles.freightNote}>
              {t('project.quote_pdf.freight_rates_note')}
            </Text>
          </View>
        ) : null}

        {hasOrderConfig ? (
          <View style={styles.sectionSpacing}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>
                {t('project.quote_pdf.order_defaults')}
              </Text>
            </View>
            <GridSection columns={4} cells={orderConfigItems} />
          </View>
        ) : null}

        {hasAddOns ? (
          <View style={styles.sectionSpacing}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>
                {t('project.quote_pdf.add_ons')}
              </Text>
            </View>
            <View style={styles.materialsTableHeader}>
              <Text style={[styles.materialsTableHeaderCell, styles.colAddOn]}>
                {t('project.quote_pdf.add_on')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colAddOnQty]}>
                {t('project.quote_pdf.qty_or_percent')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colAddOnBasis]}>
                {t('project.quote_pdf.basis')}
              </Text>
              <Text style={[styles.materialsTableHeaderCell, styles.colAddOnAmount]}>
                {t('project.quote_pdf.amount')}
              </Text>
            </View>
            {addOnRows.map((row, index) => (
              <View
                key={row.id}
                style={index % 2 === 0 ? styles.materialsRow : styles.materialsRowAlt}
              >
                <Text style={[styles.materialsCell, styles.colAddOn]}>{row.name}</Text>
                <Text style={[styles.materialsCell, styles.colAddOnQty]}>
                  {row.quantityOrPercent}
                </Text>
                <Text style={[styles.materialsCell, styles.colAddOnBasis]}>
                  {row.basis || '—'}
                </Text>
                <Text style={[styles.materialsCell, styles.colAddOnAmount]}>
                  {row.amount}
                </Text>
              </View>
            ))}
            {hasAnyAddOnAmount ? (
              <View style={styles.materialsSubtotalRow}>
                <Text
                  style={[styles.materialsSubtotalCell, styles.colAddOnSubtotalLabel]}
                >
                  {t('project.quote_pdf.add_on_subtotal')}
                </Text>
                <Text style={[styles.materialsSubtotalCell, styles.colAddOnAmount]}>
                  {currencyFormatter(addOnSubtotal.toNumber())}
                </Text>
              </View>
            ) : null}
          </View>
        ) : null}

        {hasPricing ? (
          <View style={styles.sectionSpacing}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>
                {t('project.quote_pdf.pricing')}
              </Text>
            </View>
            <View style={styles.pricingContent}>
              {hasCustomerRate ? (
                <View style={styles.pricingRow}>
                  <Text style={styles.pricingLabel}>
                    {t('project.quote_pdf.customer_rate')}
                  </Text>
                  <Text style={styles.pricingValue}>{customerRateDisplay}</Text>
                </View>
              ) : null}
              {hasComputableTax ? (
                <View style={styles.pricingRow}>
                  <Text style={styles.pricingLabel}>
                    {t('project.quote_pdf.tax')} {formatPercentage(project.taxRate)}
                    {taxBasisSuffix}
                  </Text>
                  <Text style={styles.pricingValue}>
                    {currencyFormatter(taxAmount.toNumber())}
                  </Text>
                </View>
              ) : null}
            </View>
          </View>
        ) : null}

        {hasNotes ? (
          <View style={styles.sectionSpacing}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>{t('project.quote_pdf.notes')}</Text>
            </View>
            <View style={styles.notesContent}>
              {projectNotes ? (
                <>
                  <Text style={styles.notesSubHeader}>
                    {t('project.quote_pdf.project_notes')}
                  </Text>
                  <Text style={styles.notesText}>{projectNotes}</Text>
                </>
              ) : null}
              {orderNotes ? (
                <>
                  <Text style={styles.notesSubHeader}>
                    {t('project.quote_pdf.order_notes')}
                  </Text>
                  <Text style={styles.notesText}>{orderNotes}</Text>
                </>
              ) : null}
            </View>
          </View>
        ) : null}

        {effectiveTerms ? (
          <View style={styles.sectionSpacing}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>
                {t('project.quote_pdf.terms.title')}
              </Text>
            </View>
            <View style={styles.notesContent}>
              <Text style={styles.termsText}>{effectiveTerms}</Text>
            </View>
          </View>
        ) : null}

        <Text style={styles.signatureHeading}>
          {t('project.quote_pdf.signature.accepted_by')}
        </Text>
        <View style={styles.signatureContainer}>
          <View style={styles.signatureBlock}>
            <Text style={styles.signatureLabel}>
              {t('project.quote_pdf.signature.company')}
            </Text>
            <View style={styles.signatureLine} />
          </View>
          <View style={styles.signatureBlock}>
            <Text style={styles.signatureLabel}>
              {t('project.quote_pdf.signature.name')}
            </Text>
            <View style={styles.signatureLine} />
          </View>
        </View>
        <View style={styles.signatureContainer}>
          <View style={styles.signatureBlock}>
            <Text style={styles.signatureLabel}>
              {t('project.quote_pdf.signature.title')}
            </Text>
            <View style={styles.signatureLine} />
          </View>
          <View style={styles.signatureBlock}>
            <Text style={styles.signatureLabel}>
              {t('project.quote_pdf.signature.date')}
            </Text>
            <View style={styles.signatureLine} />
          </View>
        </View>

        <View style={styles.footerRule} />
        <View style={styles.footerContainer}>
          <View style={styles.footerLeft}>
            {treadLogoBase64 ? (
              <Image src={treadLogoBase64} style={styles.footerLogo} />
            ) : null}
            <View style={styles.footerLeftText}>
              <Text style={styles.footerTagline}>
                {t('project.quote_pdf.footer_tagline')}
              </Text>
              <Link src="https://tread.ai" style={styles.footerUrl}>
                {t('project.quote_pdf.footer_url')}
              </Link>
            </View>
          </View>
          <View style={styles.footerRight}>
            <Text style={styles.footerCta}>{t('project.quote_pdf.footer_cta')}</Text>
            <Text style={styles.footerCtaSub}>
              {t('project.quote_pdf.footer_cta_sub')}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}
